import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { webinarService } from '../../../services/webinarService';
import { useAuth } from '../../../contexts/AuthContext';
import WebinarFormModal from './WebinarFormModal';
import ExpertFormModal from './ExpertFormModal';
import WebinarDetailsModal from './WebinarDetailsModal';
import JitsiMeetRoom from '../../../components/JitsiMeetRoom';

const WebinarManagementPanel = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('webinars');
  const [webinars, setWebinars] = useState([]);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWebinarModalOpen, setIsWebinarModalOpen] = useState(false);
  const [isExpertModalOpen, setIsExpertModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [error, setError] = useState('');
  const [activeJitsiWebinar, setActiveJitsiWebinar] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeView]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (activeView === 'webinars') {
        const data = await webinarService?.getWebinars();
        setWebinars(data || []);
      } else {
        const data = await webinarService?.getExperts();
        setExperts(data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebinar = () => {
    setSelectedWebinar(null);
    setIsWebinarModalOpen(true);
  };

  const handleEditWebinar = (webinar) => {
    setSelectedWebinar(webinar);
    setIsWebinarModalOpen(true);
  };

  const handleDeleteWebinar = async (id) => {
    if (!confirm('Are you sure you want to delete this webinar?')) return;

    try {
      await webinarService?.deleteWebinar(id);
      await loadData();
    } catch (err) {
      console.error('Error deleting webinar:', err);
      setError('Failed to delete webinar');
    }
  };

  const handleViewDetails = (webinar) => {
    setSelectedWebinar(webinar);
    setIsDetailsModalOpen(true);
  };

  const handleCreateExpert = () => {
    setSelectedExpert(null);
    setIsExpertModalOpen(true);
  };

  const handleEditExpert = (expert) => {
    setSelectedExpert(expert);
    setIsExpertModalOpen(true);
  };

  const handleDeleteExpert = async (id) => {
    if (!confirm('Are you sure you want to delete this expert profile?')) return;

    try {
      await webinarService?.deleteExpert(id);
      await loadData();
    } catch (err) {
      console.error('Error deleting expert:', err);
      setError('Failed to delete expert profile');
    }
  };

  const handleWebinarSubmit = async (data) => {
    try {
      if (selectedWebinar) {
        await webinarService?.updateWebinar(selectedWebinar?.id, data);
      } else {
        await webinarService?.createWebinar({ ...data, created_by: user?.id });
      }
      setIsWebinarModalOpen(false);
      await loadData();
    } catch (err) {
      console.error('Error saving webinar:', err);
      throw err;
    }
  };

  const handleExpertSubmit = async (data) => {
    try {
      if (selectedExpert) {
        await webinarService?.updateExpert(selectedExpert?.id, data);
      } else {
        await webinarService?.createExpert(data);
      }
      setIsExpertModalOpen(false);
      await loadData();
    } catch (err) {
      console.error('Error saving expert:', err);
      throw err;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live':
        return 'text-error';
      case 'upcoming':
        return 'text-primary';
      case 'completed':
        return 'text-success';
      case 'cancelled':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const getJitsiRoomName = (webinar) => {
    const safe = webinar?.title
      ? webinar?.title?.replace(/[^a-zA-Z0-9\-_]/g, '-')?.substring(0, 40)
      : `Webinar`;
    return `StudyExpo-Webinar-${safe}-${webinar?.id?.substring(0, 8)}`;
  };

  const handleJoinWebinar = (webinar) => {
    setActiveJitsiWebinar(webinar);
  };

  const handleShareLink = (webinar) => {
    const roomName = getJitsiRoomName(webinar);
    const safeRoom = roomName?.replace(/[^a-zA-Z0-9\-_]/g, '-');
    const link = `https://meet.jit.si/${safeRoom}`;
    navigator.clipboard?.writeText(link)?.then(() => {
      setCopiedId(webinar?.id);
      setTimeout(() => setCopiedId(null), 2000);
    })?.catch(() => {
      // fallback for browsers that block clipboard
      const el = document.createElement('textarea');
      el.value = link;
      document.body?.appendChild(el);
      el?.select();
      document.execCommand('copy');
      document.body?.removeChild(el);
      setCopiedId(webinar?.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* Jitsi Room Overlay */}
      {activeJitsiWebinar && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-white">
            <div className="flex items-center gap-2">
              <Icon name="Video" size={18} color="var(--color-primary)" />
              <span className="font-semibold text-sm">{activeJitsiWebinar?.title}</span>
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${activeJitsiWebinar?.status === 'live' ? 'bg-red-600 text-white' : 'bg-primary/20 text-primary'}`}>
                {activeJitsiWebinar?.status === 'live' ? '🔴 LIVE' : 'Upcoming'}
              </span>
            </div>
            <button
              onClick={() => setActiveJitsiWebinar(null)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-error hover:bg-error/80 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Icon name="X" size={14} color="white" />
              Leave & Close
            </button>
          </div>
          <div className="flex-1">
            <JitsiMeetRoom
              roomName={getJitsiRoomName(activeJitsiWebinar)}
              displayName={user?.email ? `Admin (${user?.email})` : 'Admin / Moderator'}
              title={activeJitsiWebinar?.title}
              onClose={() => setActiveJitsiWebinar(null)}
            />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-1">
            Webinar Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage webinars, expert talks, and speaker profiles
          </p>
        </div>
      </div>
      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeView === 'webinars' ? 'default' : 'outline'}
          size="sm"
          iconName="Video"
          iconPosition="left"
          onClick={() => setActiveView('webinars')}
        >
          Webinars
        </Button>
        <Button
          variant={activeView === 'experts' ? 'default' : 'outline'}
          size="sm"
          iconName="Users"
          iconPosition="left"
          onClick={() => setActiveView('experts')}
        >
          Experts
        </Button>
      </div>
      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {/* Webinars View */}
      {activeView === 'webinars' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Webinar Sessions</h3>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={handleCreateWebinar}
            >
              Add Webinar
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading webinars...</p>
            </div>
          ) : webinars?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Video" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-muted-foreground">No webinars found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Expert</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Scheduled</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Participants</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {webinars?.map((webinar) => (
                    <tr key={webinar?.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{webinar?.title}</p>
                          <p className="text-xs text-muted-foreground">{webinar?.topic}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground">{webinar?.expert?.name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground">{formatDate(webinar?.scheduled_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium capitalize ${getStatusColor(webinar?.status)}`}>
                          {webinar?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground">{webinar?.participant_count || 0}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {/* Join button — only for live or upcoming */}
                          {(webinar?.status === 'live' || webinar?.status === 'upcoming') && (
                            <button
                              onClick={() => handleJoinWebinar(webinar)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                                webinar?.status === 'live' ?'bg-error text-white hover:bg-error/80' :'bg-primary text-white hover:bg-primary/80'
                              }`}
                              title="Join Webinar as Moderator"
                            >
                              <Icon name="Video" size={13} color="white" />
                              {webinar?.status === 'live' ? 'Join Live' : 'Join'}
                            </button>
                          )}
                          {/* Share Link button — only for live or upcoming */}
                          {(webinar?.status === 'live' || webinar?.status === 'upcoming') && (
                            <button
                              onClick={() => handleShareLink(webinar)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                                copiedId === webinar?.id
                                  ? 'bg-success text-white' :'bg-muted border border-border text-foreground hover:bg-border'
                              }`}
                              title="Copy Jitsi room link to share with external experts"
                            >
                              <Icon name={copiedId === webinar?.id ? 'Check' : 'Link'} size={13} color={copiedId === webinar?.id ? 'white' : 'var(--color-foreground)'} />
                              {copiedId === webinar?.id ? 'Copied!' : 'Share Link'}
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(webinar)}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title="View Details"
                          >
                            <Icon name="Eye" size={16} color="var(--color-foreground)" />
                          </button>
                          <button
                            onClick={() => handleEditWebinar(webinar)}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title="Edit"
                          >
                            <Icon name="Edit" size={16} color="var(--color-primary)" />
                          </button>
                          <button
                            onClick={() => handleDeleteWebinar(webinar?.id)}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={16} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Experts View */}
      {activeView === 'experts' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Expert Profiles</h3>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={handleCreateExpert}
            >
              Add Expert
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading experts...</p>
            </div>
          ) : experts?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-muted-foreground">No experts found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {experts?.map((expert) => (
                <div key={expert?.id} className="bg-muted/50 border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground truncate">{expert?.name}</h4>
                      <p className="text-xs text-muted-foreground truncate">{expert?.title}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{expert?.bio}</p>
                  {expert?.expertise_areas?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {expert?.expertise_areas?.slice(0, 2)?.map((area, index) => (
                        <span key={index} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                          {area}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleEditExpert(expert)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Edit"
                    >
                      <Icon name="Edit" size={16} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => handleDeleteExpert(expert?.id)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-error)" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Modals */}
      <WebinarFormModal
        isOpen={isWebinarModalOpen}
        onClose={() => setIsWebinarModalOpen(false)}
        onSubmit={handleWebinarSubmit}
        webinar={selectedWebinar}
        experts={experts}
      />
      <ExpertFormModal
        isOpen={isExpertModalOpen}
        onClose={() => setIsExpertModalOpen(false)}
        onSubmit={handleExpertSubmit}
        expert={selectedExpert}
      />
      <WebinarDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        webinar={selectedWebinar}
      />
    </div>
  );
};

export default WebinarManagementPanel;